import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const ReferrerPanelPage = () => {
  const { referrer } = useParams();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('BTC');
  const [paymentDetails, setPaymentDetails] = useState('');
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');


  const COMPLETED_STATUSES = ['finished', 'early_confirmed', 'confirmed'];
  const INCOMPLETE_STATUSES = ['waiting'];

  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const [chartMode, setChartMode] = useState('month');

  useEffect(() => {
    const token = localStorage.getItem(`ref_token_${referrer}`);
    if (!token) {
      navigate(`/ref/${referrer}/login`);
      return;
    }
    
    // Fetch payments
    fetch(`/api/referrer/${referrer}/payments?token=${token}`)
      .then(res => res.json())
      .then(data => {
        if (data.success) setPayments(data.payments);
        else setError(data.message || 'Failed to load payments');
      })
      .catch(() => setError('Network error'))
      .finally(() => setLoading(false));
    
    // Fetch referrer info
    fetch(`/api/referrer/${referrer}`)
      .then(res => res.json())
      .then(data => {
        if (data.success) setReferrerInfo(data.referrer);
      });
    
    // Fetch notifications
    fetch(`/api/referrer/${referrer}/notifications?token=${token}`)
      .then(res => res.json())
      .then(data => {
        if (data.success) setNotifications(data.notifications);
      })
      .catch(err => console.error('Error fetching notifications:', err));
    
    // Fetch payouts
    fetch(`/api/referrer/${referrer}/payouts?token=${token}`)
      .then(res => res.json())
      .then(data => {
        if (data.success) setPayouts(data.payouts);
      })
             .catch(err => console.error('Error fetching payouts:', err));
  }, [referrer, navigate]);



  const handleLogout = () => {
    localStorage.removeItem(`ref_token_${referrer}`);
    navigate(`/ref/${referrer}/login`);
  };

  const handlePayoutRequest = async () => {
    if (!payoutAmount || !paymentDetails) {
      alert('Please fill in all fields');
      return;
    }

    const amount = parseFloat(payoutAmount);
    if (amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    if (amount > (referrerInfo?.balance || 0)) {
      alert(`Insufficient balance. Current balance: $${(referrerInfo?.balance || 0).toFixed(2)}`);
      return;
    }

    if (amount < 10) {
      alert('Minimum payout amount is $10.00');
      return;
    }

    setPayoutLoading(true);
    try {
      const token = localStorage.getItem(`ref_token_${referrer}`);
      const response = await fetch(`/api/referrer/${referrer}/payout?token=${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount,
          payment_method: paymentMethod,
          payment_details: paymentDetails
        })
      });

      const data = await response.json();
      
      if (data.success) {
        alert('Payout request submitted successfully! We will process it within 1-3 business days.');
        setShowPayoutModal(false);
        setPayoutAmount('');
        setPaymentDetails('');
        
        // Update referrer info with new balance
        setReferrerInfo(prev => ({
          ...prev,
          balance: data.new_balance
        }));
      } else {
        alert(data.message || 'Failed to submit payout request');
      }
    } catch (error) {
      alert('Error submitting payout request. Please try again.');
      console.error('Payout error:', error);
    } finally {
      setPayoutLoading(false);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const token = localStorage.getItem(`ref_token_${referrer}`);
      const response = await fetch(`/api/referrer/${referrer}/notifications/${notificationId}?token=${token}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const completedPayments = payments.filter(p => COMPLETED_STATUSES.includes(p.status));
  const filteredPayments = completedPayments.filter(p =>
    (planFilter === 'all' || p.plan_id === planFilter) &&
    (statusFilter === 'all' || p.status === statusFilter)
  );
  const incompletePayments = payments.filter(p => INCOMPLETE_STATUSES.includes(p.status));
  const totalRevenue = completedPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const percent = referrerInfo?.percent || 0;
  const yourEarnings = ((totalRevenue * percent) / 100).toFixed(2);

  // Helper: group payments by month
  function getPaymentsPerMonth(payments) {
    const months = {};
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      months[key] = 0;
    }
    payments.forEach(p => {
      const d = new Date(p.created_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (months[key] !== undefined) months[key]++;
    });
    return Object.entries(months).map(([month, count]) => ({ month, count }));
  }

  // Helper: group payments by week with readable range labels
  function getPaymentsPerWeek(payments) {
    const weeks = {};
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (i * 7));
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      const label = `${String(start.getDate()).padStart(2, '0')}-${String(start.getMonth() + 1).padStart(2, '0')} ~ ${String(end.getDate()).padStart(2, '0')}-${String(end.getMonth() + 1).padStart(2, '0')}`;
      weeks[label] = 0;
    }
    payments.forEach(p => {
      const d = new Date(p.created_at);
      for (const label in weeks) {
        const [startStr, endStr] = label.split(' ~ ');
        const [startD, startM] = startStr.split('-').map(Number);
        const [endD, endM] = endStr.split('-').map(Number);
        const year = d.getFullYear();
        const start = new Date(year, startM - 1, startD);
        const end = new Date(year, endM - 1, endD, 23, 59, 59, 999);
        if (d >= start && d <= end) {
          weeks[label]++;
          break;
        }
      }
    });
    return Object.entries(weeks).map(([week, count]) => ({ week, count }));
  }

  // Helper: get ISO week number
  function getWeekNumber(d) {
    d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(),0,1));
    return Math.ceil((((d - yearStart) / 86400000) + 1)/7);
  }

  // Helper: plan breakdown
  function getPlanBreakdown(payments) {
    const plans = { monthly: 0, quarterly: 0, lifetime: 0 };
    payments.forEach(p => {
      if (plans[p.plan_id] !== undefined) plans[p.plan_id]++;
    });
    return Object.entries(plans).map(([plan, value]) => ({ name: plan, value }));
  }

  const paymentsPerMonth = getPaymentsPerMonth(completedPayments);
  const paymentsPerWeek = getPaymentsPerWeek(completedPayments);
  const planBreakdown = getPlanBreakdown(completedPayments);
  const pieColors = ['#6ea8ff', '#ffb347', '#4ade80'];

  // Responsive styles for mobile
  const mobileContainer = {
    maxWidth: 1200,
    margin: '0 auto',
    background: '#182447',
    borderRadius: 18,
    boxShadow: '0 8px 32px #2d6cff33',
    padding: '2.5rem 2rem',
    width: '100%',
  };
  const mobileFlex = {
    display: 'flex',
    gap: '2.5rem',
    marginBottom: 32,
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
  };
  const mobileStatCard = {
    background: '#22305a',
    borderRadius: 12,
    padding: '1.5rem 2.2rem',
    color: '#fff',
    flex: 1,
    textAlign: 'center',
    boxShadow: '0 0 12px #2d6cff44',
    minWidth: 180,
    marginBottom: 16,
  };
  const mobileChartCard = {
    background: '#22305a',
    borderRadius: 12,
    padding: '1.5rem 1.5rem',
    color: '#fff',
    minWidth: 240,
    minHeight: 220,
    flex: 1,
    boxShadow: '0 0 12px #2d6cff44',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0e1633 0%, #1e2a4a 100%)', padding: '3rem 0' }}>
      <div style={mobileContainer}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 24,
          gap: '1rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
            {referrerInfo?.logo_url && (
              <img src={referrerInfo.logo_url} alt={referrerInfo.name} style={{ width: 60, height: 60, objectFit: 'contain', borderRadius: 12, background: '#fff', boxShadow: '0 0 12px #2d6cff66' }} />
            )}
            <h2 style={{ color: '#6ea8ff', fontWeight: 900, fontSize: 'clamp(2rem, 6vw, 2.5rem)', textShadow: '0 0 12px #2d6cffcc', margin: 0, wordBreak: 'break-word', letterSpacing: '0.5px' }}>
              {referrerInfo?.name || 'Referrer Panel'}
            </h2>
          </div>
          
          {/* Right side: Balance, Logout */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {/* Clickable Balance Display */}
            <button
              onClick={() => setShowPayoutModal(true)}
              disabled={(referrerInfo?.balance || 0) < 10}
              style={{
                background: (referrerInfo?.balance || 0) >= 10 
                  ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)' 
                  : 'linear-gradient(135deg, #6c757d 0%, #5a6268 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '0.5rem 0.75rem',
                textAlign: 'center',
                cursor: (referrerInfo?.balance || 0) >= 10 ? 'pointer' : 'not-allowed',
                boxShadow: '0 0 8px rgba(40, 167, 69, 0.3)',
                transition: 'all 0.2s ease',
                fontSize: '0.9rem'
              }}
              title={(referrerInfo?.balance || 0) < 10 ? 'Minimum withdrawal: $10.00' : 'Click to withdraw'}
            >
              <div style={{ fontWeight: 700 }}>
                ${(referrerInfo?.balance || 0).toFixed(2)}
              </div>
              <div style={{ fontSize: '0.7rem', opacity: 0.9 }}>
                {(referrerInfo?.balance || 0) >= 10 ? 'Withdraw' : 'Balance'}
              </div>
            </button>
             

             
             {/* Logout Button */}
             <button
               onClick={handleLogout}
               style={{
                 background: '#ff6b6b',
                 color: '#fff',
                 border: 'none',
                 borderRadius: 6,
                 padding: '0.5rem 0.75rem',
                 fontWeight: 600,
                 fontSize: '0.85rem',
                 cursor: 'pointer',
                 boxShadow: '0 0 6px #ff6b6b55',
                 transition: 'all 0.2s ease'
               }}
             >
               ✕ Logout
             </button>
           </div>
        </div>
        
        {/* Notification Banners */}
        {notifications.length > 0 && notifications.map(notification => (
          <div key={notification.id} style={{
            background: notification.status === 'completed' 
              ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)' 
              : 'linear-gradient(135deg, #dc3545 0%, #fd7e14 100%)',
            color: '#fff',
            borderRadius: 12,
            padding: '1rem 1.5rem',
            marginBottom: '1rem',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: `2px solid ${notification.status === 'completed' ? '#20c997' : '#fd7e14'}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
              <span style={{ fontSize: '1.5rem' }}>
                {notification.status === 'completed' ? '🎉' : '⚠️'}
              </span>
              <div>
                                 <div style={{ 
                   fontWeight: 700, 
                   fontSize: '1rem', 
                   marginBottom: '0.25rem'
                 }}>
                   {notification.status === 'completed' ? 'Payout Completed!' : 'Payout Rejected'}
                 </div>
                 <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: notification.status === 'rejected' ? '0.5rem' : '0' }}>
                   ${notification.amount.toFixed(2)} via {notification.payment_method} • {new Date(notification.processed_at).toLocaleDateString()}
                 </div>
                 {notification.status === 'rejected' && (
                   <div style={{ 
                     fontSize: '0.85rem', 
                     opacity: 0.95,
                     padding: '0.5rem 0.75rem',
                     background: 'rgba(255,255,255,0.15)',
                     borderRadius: '6px',
                     marginTop: '0.5rem'
                   }}>
                     📞 Need help? Contact: <a 
                       href="https://t.me/deserved0" 
                       style={{ color: '#fff', textDecoration: 'underline', fontWeight: 600 }}
                       target="_blank"
                       rel="noopener noreferrer"
                     >@deserved0</a> • <a 
                       href="https://t.me/tomnookbank" 
                       style={{ color: '#fff', textDecoration: 'underline', fontWeight: 600 }}
                       target="_blank"
                       rel="noopener noreferrer"
                     >@tomnookbank</a>
                   </div>
                 )}
              </div>
            </div>
            <button
              onClick={() => deleteNotification(notification.id)}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '1.2rem',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                fontWeight: 700
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255,255,255,0.3)';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255,255,255,0.2)';
                e.target.style.transform = 'scale(1)';
              }}
              title="Dismiss notification"
            >
              ✕
            </button>
          </div>
        ))}
        
        {loading ? (
          <div style={{ color: '#b6d0ff', textAlign: 'center', fontSize: '1.2rem' }}>Loading...</div>
        ) : error ? (
          <div style={{ color: '#ff6b6b', textAlign: 'center', fontWeight: 600 }}>{error}</div>
        ) : (
          <>
            {/* Tab Navigation */}
            <div style={{
              display: 'flex',
              gap: 0,
              marginBottom: '2rem',
              background: '#22305a',
              borderRadius: 12,
              padding: '0.5rem',
              boxShadow: '0 0 12px #2d6cff22',
              margin: '0 auto 2rem auto'
            }}>
              <button
                onClick={() => setActiveTab('dashboard')}
                style={{
                  background: activeTab === 'dashboard' ? '#6ea8ff' : 'transparent',
                  color: activeTab === 'dashboard' ? '#fff' : '#b6d0ff',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  fontWeight: 700,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  borderRadius: 8,
                  transition: 'all 0.2s'
                }}
              >
                📊 Dashboard
              </button>
              <button
                onClick={() => setActiveTab('payouts')}
                style={{
                  background: activeTab === 'payouts' ? '#6ea8ff' : 'transparent',
                  color: activeTab === 'payouts' ? '#fff' : '#b6d0ff',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  fontWeight: 700,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  borderRadius: 8,
                  transition: 'all 0.2s'
                }}
              >
                📋 Payouts ({payouts.length})
              </button>
            </div>

            {activeTab === 'dashboard' && (
              <>
                {/* Charts Section */}
            <div style={mobileFlex}>
              <div style={mobileChartCard}>
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ display: 'flex', gap: 0, background: '#1a2542', borderRadius: 8, overflow: 'hidden', boxShadow: '0 0 6px #2d6cff22', marginBottom: 8 }}>
                    <button
                      onClick={() => setChartMode('month')}
                      style={{
                        background: chartMode === 'month' ? '#6ea8ff' : 'transparent',
                        color: chartMode === 'month' ? '#fff' : '#b6d0ff',
                        border: 'none',
                        padding: '0.45rem 1.5rem',
                        fontWeight: 700,
                        fontSize: '1rem',
                        cursor: 'pointer',
                        borderRadius: 0,
                        transition: 'background 0.2s',
                        outline: 'none',
                      }}
                    >
                      Month
                    </button>
                    <button
                      onClick={() => setChartMode('week')}
                      style={{
                        background: chartMode === 'week' ? '#6ea8ff' : 'transparent',
                        color: chartMode === 'week' ? '#fff' : '#b6d0ff',
                        border: 'none',
                        padding: '0.45rem 1.5rem',
                        fontWeight: 700,
                        fontSize: '1rem',
                        cursor: 'pointer',
                        borderRadius: 0,
                        transition: 'background 0.2s',
                        outline: 'none',
                      }}
                    >
                      Week
                    </button>
                  </div>
                </div>
                {completedPayments.length === 0 ? (
                  <div style={{ color: '#b6d0ff', textAlign: 'center' }}>No payments yet</div>
                ) : (
                  <ResponsiveContainer width="100%" height={160}>
                    <BarChart data={chartMode === 'month' ? paymentsPerMonth : paymentsPerWeek} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                      <XAxis dataKey={chartMode === 'month' ? 'month' : 'week'} tick={{ fill: '#b6d0ff', fontSize: 12 }} interval={0} angle={-18} dy={10} height={40} />
                      <YAxis allowDecimals={false} tick={{ fill: '#b6d0ff', fontSize: 12 }} />
                      <Tooltip contentStyle={{ background: '#22305a', border: 'none', color: '#fff' }} />
                      <Bar dataKey="count" fill="#6ea8ff" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
            <div style={{ ...mobileFlex, flexWrap: 'wrap' }}>
              <div style={mobileStatCard}>
                <div style={{ fontSize: '2.2rem', fontWeight: 800 }}>{completedPayments.length}</div>
                <div style={{ color: '#b6d0ff', fontWeight: 600, marginTop: 6 }}>Completed Payments</div>
              </div>
              <div style={mobileStatCard}>
                <div style={{ fontSize: '2.2rem', fontWeight: 800 }}>${totalRevenue}</div>
                <div style={{ color: '#b6d0ff', fontWeight: 600, marginTop: 6 }}>Total Revenue</div>
              </div>
              <div style={mobileStatCard}>
                <div style={{ fontSize: '2.2rem', fontWeight: 800 }}>${yourEarnings}</div>
                <div style={{ color: '#b6d0ff', fontWeight: 600, marginTop: 6 }}>Your Earnings ({percent}%)</div>
              </div>
            </div>
            {/* Filters */}
            <div style={{ display: 'flex', gap: '1.5rem', marginBottom: 18, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ minWidth: 120 }}>
                <label style={{ color: '#b6d0ff', fontWeight: 600, marginRight: 8 }}>Plan:</label>
                <select value={planFilter} onChange={e => setPlanFilter(e.target.value)} style={{ padding: '0.4rem 0.8rem', borderRadius: 6, border: '1px solid #2d6cff44', background: '#22305a', color: '#b6d0ff', minWidth: 100 }}>
                  <option value="all">All</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="lifetime">Lifetime</option>
                  <option value="camera_lifetime">Camera</option>
                </select>
              </div>
            </div>
            {/* Completed Payments Table */}
            <div style={{ overflowX: 'auto', maxHeight: 420, marginBottom: 32 }}>
              <table style={{ width: '100%', minWidth: 700, borderCollapse: 'collapse', background: '#22305a', borderRadius: 12, overflow: 'hidden', color: '#fff', boxShadow: '0 0 12px #2d6cff22', fontSize: 'clamp(0.85rem, 2.5vw, 1rem)' }}>
                <thead>
                  <tr style={{ background: '#2d6cff', color: '#fff' }}>
                    <th style={{ padding: '0.9rem', textAlign: 'center' }}>Date</th>
                    <th style={{ padding: '0.9rem', textAlign: 'center' }}>Amount</th>
                    <th style={{ padding: '0.9rem', textAlign: 'center' }}>Plan</th>
                    <th style={{ padding: '0.9rem', textAlign: 'center' }}>Status</th>
                    <th style={{ padding: '0.9rem', textAlign: 'center' }}>Payment ID</th>
                    <th style={{ padding: '0.9rem', textAlign: 'center' }}>Crypto</th>
                    <th style={{ padding: '0.9rem', textAlign: 'center' }}>Payin Hash</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.slice(0, 10).map(p => {
                    // Blockchain explorer links by crypto_currency
                    let explorer = '';
                    const hash = p.payin_hash;
                    const crypto = (p.crypto_currency || '').toLowerCase();
                    if (hash) {
                      if (crypto === 'btc' || crypto === 'bitcoin') explorer = `https://www.blockchain.com/btc/tx/${hash}`;
                      else if (crypto === 'ltc' || crypto === 'litecoin') explorer = `https://blockchair.com/litecoin/transaction/${hash}`;
                      else if (crypto === 'eth' || crypto === 'ethereum') explorer = `https://etherscan.io/tx/${hash}`;
                      else if (crypto === 'trx' || crypto === 'tron') explorer = `https://tronscan.org/#/transaction/${hash}`;
                      else explorer = '';
                    }
                    return (
                      <tr key={p.payment_id} style={{ borderBottom: '1px solid #2d6cff44' }}>
                        <td style={{ padding: '0.7rem', color: '#b6d0ff', whiteSpace: 'nowrap', textAlign: 'center' }}>{new Date(p.created_at).toLocaleString()}</td>
                        <td style={{ padding: '0.7rem', color: '#fff', fontWeight: 700, textAlign: 'center' }}>${p.amount}</td>
                        <td style={{ padding: '0.7rem', color: '#b6d0ff', textAlign: 'center' }}>{p.plan_id}</td>
                        <td style={{ padding: '0.7rem', color: '#4ade80', fontWeight: 600, textAlign: 'center' }}>completed</td>
                        <td style={{ padding: '0.7rem', color: '#b6d0ff', fontFamily: 'monospace', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }} title={p.payment_id}>{p.payment_id}</td>
                        <td style={{ padding: '0.7rem', color: '#b6d0ff', fontFamily: 'monospace', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>{p.crypto_amount && p.crypto_currency ? `${p.crypto_amount} ${p.crypto_currency}` : ''}</td>
                        <td style={{ padding: '0.7rem', color: '#b6d0ff', fontFamily: 'monospace', textAlign: 'center', position: 'relative' }}>
                          {hash && explorer ? (
                            <div style={{ display: 'inline-block', position: 'relative' }}>
                              <a
                                href={explorer}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'inline-block',
                                  background: '#2d6cff',
                                  color: '#fff',
                                  borderRadius: 6,
                                  padding: '0.32rem 0.9rem',
                                  fontWeight: 600,
                                  fontSize: '0.98rem',
                                  textDecoration: 'none',
                                  cursor: 'pointer',
                                  boxShadow: '0 0 8px #2d6cff55',
                                  border: 'none',
                                  outline: 'none',
                                  position: 'relative',
                                }}
                                onMouseEnter={e => {
                                  const tooltip = e.currentTarget.nextSibling;
                                  if (tooltip) tooltip.style.display = 'block';
                                }}
                                onMouseLeave={e => {
                                  const tooltip = e.currentTarget.nextSibling;
                                  if (tooltip) tooltip.style.display = 'none';
                                }}
                              >
                                Track
                              </a>
                              <span
                                style={{
                                  display: 'none',
                                  position: 'absolute',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  bottom: '120%',
                                  background: '#222e4d',
                                  color: '#fff',
                                  padding: '0.5rem 1rem',
                                  borderRadius: 8,
                                  fontSize: '0.98rem',
                                  whiteSpace: 'pre',
                                  zIndex: 10,
                                  boxShadow: '0 2px 12px #2d6cff33',
                                  minWidth: 220,
                                  textAlign: 'center',
                                }}
                              >
                                {hash}
                              </span>
                            </div>
                          ) : hash ? (
                            <span title={hash} style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>{hash.length > 16 ? hash.slice(0, 12) + '...' : hash}</span>
                          ) : ''}
                        </td>
                      </tr>
                    );
                  })}
                  {filteredPayments.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', color: '#b6d0ff', padding: '1.5rem' }}>No completed payments found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Incomplete Payments Section */}
            {incompletePayments.length > 0 && (
              <div style={{ marginTop: 32 }}>
                <h3 style={{ color: '#ffb347', fontWeight: 800, marginBottom: 12, fontSize: 'clamp(1.1rem, 4vw, 1.3rem)' }}>Payments Not Completed</h3>
                <div style={{ overflowX: 'auto', maxHeight: 320 }}>
                  <table style={{ width: '100%', minWidth: 700, borderCollapse: 'collapse', background: '#2d1e33', borderRadius: 12, overflow: 'hidden', color: '#fff', boxShadow: '0 0 12px #2d6cff22', fontSize: 'clamp(0.85rem, 2.5vw, 1rem)' }}>
                    <thead>
                      <tr style={{ background: '#ffb347', color: '#222' }}>
                        <th style={{ padding: '0.8rem', textAlign: 'center' }}>Date</th>
                        <th style={{ padding: '0.8rem', textAlign: 'center' }}>Amount</th>
                        <th style={{ padding: '0.8rem', textAlign: 'center' }}>Plan</th>
                        <th style={{ padding: '0.8rem', textAlign: 'center' }}>Status</th>
                        <th style={{ padding: '0.8rem', textAlign: 'center' }}>Payment ID</th>
                        <th style={{ padding: '0.8rem', textAlign: 'center' }}>Crypto</th>
                        <th style={{ padding: '0.8rem', textAlign: 'center' }}>Payin Hash</th>
                      </tr>
                    </thead>
                    <tbody>
                      {incompletePayments.slice(0, 10).map(p => (
                        <tr key={p.payment_id} style={{ borderBottom: '1px solid #ffb34744' }}>
                          <td style={{ padding: '0.7rem', color: '#b6d0ff', textAlign: 'center' }}>{new Date(p.created_at).toLocaleString()}</td>
                          <td style={{ padding: '0.7rem', color: '#fff', fontWeight: 700, textAlign: 'center' }}>${p.amount}</td>
                          <td style={{ padding: '0.7rem', color: '#b6d0ff', textAlign: 'center' }}>{p.plan_id}</td>
                          <td style={{ padding: '0.7rem', color: '#ffb347', fontWeight: 600, textAlign: 'center' }}>{p.status}</td>
                          <td style={{ padding: '0.7rem', color: '#b6d0ff', fontFamily: 'monospace', textAlign: 'center' }}>{p.payment_id}</td>
                          <td style={{ padding: '0.7rem', color: '#b6d0ff', fontFamily: 'monospace', textAlign: 'center' }}>{p.crypto_amount && p.crypto_currency ? `${p.crypto_amount} ${p.crypto_currency}` : 'N/A'}</td>
                          <td style={{ padding: '0.7rem', color: '#b6d0ff', fontFamily: 'monospace', textAlign: 'center' }}>{p.payin_hash ? p.payin_hash : 'N/A'}</td>
                        </tr>
                      ))}
                      {incompletePayments.length === 0 && (
                        <tr><td colSpan={7} style={{ textAlign: 'center', color: '#b6d0ff', padding: '1.2rem' }}>No incomplete payments.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
              </>
            )}

            {activeTab === 'payouts' && (
              <div>
                <h3 style={{ color: '#6ea8ff', fontWeight: 800, marginBottom: '1.5rem', fontSize: 'clamp(1.2rem, 4vw, 1.5rem)' }}>
                  💳 Payout History
                </h3>
                
                {payouts.length === 0 ? (
                  <div style={{
                    background: '#22305a',
                    borderRadius: 12,
                    padding: '2rem',
                    textAlign: 'center',
                    color: '#b6d0ff',
                    boxShadow: '0 0 12px #2d6cff22'
                  }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💰</div>
                    <div style={{ fontSize: '1.2rem', fontWeight: 600, marginBottom: '0.5rem' }}>No Payouts Yet</div>
                    <div>When you request payouts, they will appear here with their status and transaction details.</div>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto', maxHeight: 600 }}>
                    <table style={{ width: '100%', minWidth: 700, borderCollapse: 'collapse', background: '#22305a', borderRadius: 12, overflow: 'hidden', color: '#fff', boxShadow: '0 0 12px #2d6cff22', fontSize: 'clamp(0.85rem, 2.5vw, 1rem)' }}>
                      <thead>
                        <tr style={{ background: '#2d6cff', color: '#fff' }}>
                          <th style={{ padding: '0.9rem', textAlign: 'center' }}>Date</th>
                          <th style={{ padding: '0.9rem', textAlign: 'center' }}>Amount</th>
                          <th style={{ padding: '0.9rem', textAlign: 'center' }}>Method</th>
                          <th style={{ padding: '0.9rem', textAlign: 'center' }}>Status</th>
                          <th style={{ padding: '0.9rem', textAlign: 'center' }}>TXID</th>
                          <th style={{ padding: '0.9rem', textAlign: 'center' }}>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payouts.map(payout => (
                          <tr key={payout.id} style={{ borderBottom: '1px solid #2d6cff44' }}>
                            <td style={{ padding: '0.7rem', color: '#b6d0ff', whiteSpace: 'nowrap', textAlign: 'center' }}>
                              {new Date(payout.created_at).toLocaleDateString()}
                              <div style={{ fontSize: '0.8rem' }}>
                                {new Date(payout.created_at).toLocaleTimeString()}
                              </div>
                            </td>
                            <td style={{ padding: '0.7rem', color: '#fff', fontWeight: 700, textAlign: 'center' }}>
                              ${payout.amount}
                            </td>
                            <td style={{ padding: '0.7rem', color: '#b6d0ff', textAlign: 'center', textTransform: 'uppercase' }}>
                              {payout.payment_method}
                            </td>
                            <td style={{ padding: '0.7rem', textAlign: 'center' }}>
                              <span style={{
                                background: payout.status === 'completed' ? '#28a745' : 
                                           payout.status === 'pending' ? '#ffc107' : '#dc3545',
                                color: payout.status === 'pending' ? '#000' : '#fff',
                                padding: '0.25rem 0.75rem',
                                borderRadius: 12,
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                textTransform: 'uppercase'
                              }}>
                                {payout.status}
                              </span>
                            </td>
                            <td style={{ padding: '0.7rem', color: '#b6d0ff', fontFamily: 'monospace', textAlign: 'center' }}>
                              {payout.txid ? (
                                <div style={{ display: 'inline-block', position: 'relative' }}>
                                  <code style={{
                                    background: '#2d6cff',
                                    color: '#fff',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: 4,
                                    fontSize: '0.8rem',
                                    cursor: 'pointer'
                                  }}
                                  title={payout.txid}
                                  onClick={() => navigator.clipboard.writeText(payout.txid)}
                                  >
                                    {payout.txid.length > 12 ? `${payout.txid.slice(0, 8)}...${payout.txid.slice(-4)}` : payout.txid}
                                  </code>
                                </div>
                              ) : (
                                <span style={{ color: '#6c757d' }}>Pending</span>
                              )}
                            </td>
                            <td style={{ padding: '0.7rem', color: '#b6d0ff', fontSize: '0.85rem', textAlign: 'center', maxWidth: 200 }}>
                              {payout.admin_notes ? (
                                <div style={{ 
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }} title={payout.admin_notes}>
                                  {payout.admin_notes}
                                </div>
                              ) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Payout Modal */}
      {showPayoutModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#182447',
            borderRadius: 18,
            padding: '2rem',
            maxWidth: 500,
            width: '100%',
            boxShadow: '0 8px 32px #2d6cff33',
            color: '#fff'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: '#6ea8ff', fontSize: '1.5rem' }}>💰 Request Crypto Payout</h3>
              <button
                onClick={() => setShowPayoutModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#b6d0ff',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '1rem', marginBottom: '0.5rem', color: '#b6d0ff' }}>
                Available Balance: <span style={{ color: '#28a745', fontWeight: 'bold' }}>${(referrerInfo?.balance || 0).toFixed(2)}</span>
              </div>
              <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                Minimum withdrawal: $10.00
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#b6d0ff' }}>
                Withdrawal Amount ($)
              </label>
              <input
                type="number"
                min="10"
                max={referrerInfo?.balance || 0}
                step="0.01"
                value={payoutAmount}
                onChange={(e) => setPayoutAmount(e.target.value)}
                placeholder="Enter amount"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #2d6cff44',
                  borderRadius: 8,
                  background: '#22305a',
                  color: '#fff',
                  fontSize: '1rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#b6d0ff' }}>
                Cryptocurrency
              </label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #2d6cff44',
                  borderRadius: 8,
                  background: '#22305a',
                  color: '#fff',
                  fontSize: '1rem'
                }}
              >
                <option value="BTC">Bitcoin (BTC)</option>
                <option value="LTC">Litecoin (LTC)</option>
                <option value="ETH">Ethereum (ETH)</option>
                <option value="USDT_TRC20">USDT (TRC-20) - Low fees</option>
                <option value="USDT_ERC20">USDT (ERC-20) - Higher fees</option>
              </select>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#b6d0ff' }}>
                Wallet Address
              </label>
              <textarea
                value={paymentDetails}
                onChange={(e) => setPaymentDetails(e.target.value)}
                placeholder={
                  paymentMethod === 'BTC' ? 'Enter your Bitcoin wallet address (e.g., 1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa)' :
                  paymentMethod === 'LTC' ? 'Enter your Litecoin wallet address (e.g., LdP8Qox1VAhCzLJNqrr74YovaWYyNBUWvL)' :
                  paymentMethod === 'ETH' ? 'Enter your Ethereum wallet address (e.g., 0x742d35Cc6634C0532925a3b8D5c3c9b5B0b85aB7)' :
                  paymentMethod === 'USDT_TRC20' ? 'Enter your USDT (TRC-20) wallet address (e.g., TEXAMPLE123456789012345678901234567890123)' :
                  paymentMethod === 'USDT_ERC20' ? 'Enter your USDT (ERC-20) wallet address (e.g., 0x742d35Cc6634C0532925a3b8D5c3c9b5B0b85aB7)' :
                  'Enter your cryptocurrency wallet address'
                }
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #2d6cff44',
                  borderRadius: 8,
                  background: '#22305a',
                  color: '#fff',
                  fontSize: '1rem',
                  resize: 'vertical',
                  fontFamily: 'monospace'
                }}
              />
              <div style={{ fontSize: '0.8rem', color: '#6c757d', marginTop: '0.5rem' }}>
                ⚠️ Double-check your wallet address. Incorrect addresses may result in permanent loss of funds.
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowPayoutModal(false)}
                style={{
                  background: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handlePayoutRequest}
                disabled={payoutLoading || !payoutAmount || !paymentDetails}
                style={{
                  background: payoutLoading ? '#6c757d' : '#28a745',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  cursor: payoutLoading ? 'not-allowed' : 'pointer',
                  opacity: payoutLoading ? 0.6 : 1
                }}
              >
                {payoutLoading ? 'Processing...' : 'Submit Request'}
              </button>
            </div>

            <div style={{ marginTop: '1rem', padding: '1rem', background: '#22305a', borderRadius: 8, fontSize: '0.85rem', color: '#b6d0ff' }}>
              <strong>🕐 Processing Time:</strong> 1-3 business days<br/>
              <strong>💰 Payment Method:</strong> Cryptocurrency transfer to your wallet<br/>
              <strong>⚠️ Important:</strong> Verify your wallet address - crypto transactions are irreversible
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferrerPanelPage; 